/* ════ ENTRY POINT ═════════════════════════════════════════
 * GRANTT — red10 resource planner.
 * Recomposes the (formerly single-file) app from focused ES modules.
 * Module scripts are deferred, so the DOM is ready when this runs.
 * ════════════════════════════════════════════════════════ */
import '../styles/main.css';

import { $ } from './dom.js';
import { APP_VERSION } from './config.js';
import { initTheme } from './theme.js';
import { initAuth } from './auth.js';
import { initViews, setView } from './views.js';
import { initFilters } from './filters.js';
import { initIdeas } from './ideas.js';
import { initHistory } from './historyview.js';
import { initCostDash } from './costdash.js';
import { initCo2 } from './co2board.js';
import { initLeave } from './leave.js';
import { initSeasonal } from './seasonal.js';
import { initDigest } from './digest.js';
import { initTravelImport } from './travelimport.js';
import { initNavigation, jumpToMonth } from './navigation.js';
import { initResourceModal, addResource, deleteResource, closeResModal, setResEmail, setResRole, setResBirthday } from './modals/resource.js';
import { initCsv } from './csv.js';
import { initImport } from './importplanner.js';
import { initWhatsNew } from './whatsnew.js';
import { initKeyboard } from './keyboard.js';
import { openNew, closeModal, pickCat, saveTask, deleteTask, duplicateTask } from './modals/task.js';
import { openIdeaModal, closeIdeaModal, pickIdeaCat, saveIdea, deleteIdea } from './modals/idea.js';
import { addSubtask, addIdeaSubtask } from './subtasks.js';
import { initTagInputs } from './tagsedit.js';
import { initCatManager, openCatManager } from './catmanager.js';

// Inline `onclick=` / `onkeydown=` handlers in index.html (and in dynamically
// generated markup) call these by bare name, so they must live on `window`.
Object.assign(window, {
  closeModal, pickCat, saveTask, deleteTask, duplicateTask, addSubtask,
  openIdeaModal, closeIdeaModal, pickIdeaCat, saveIdea, deleteIdea, addIdeaSubtask,
  closeResModal, addResource, deleteResource, setResEmail, setResRole, setResBirthday, jumpToMonth,
  openCatManager,
});

// Stamp the version on the login screen and the dashboard.
const lv = $('login-version'); if (lv) lv.textContent = APP_VERSION;
const tv = $('tb-version'); if (tv) tv.textContent = APP_VERSION;

// Wire up the modules.
initTheme();
initViews();
initFilters();
initIdeas();
initHistory();
initCostDash();
initCo2();
initLeave();
initSeasonal();
initDigest();

// Top-bar "More" menu (Export, Import, Resources): opens on click, closes on
// picking an item, clicking elsewhere or Escape.
{
  const btn = $('btn-more'), menu = $('more-menu');
  const set = open => { menu.classList.toggle('open', open); btn.setAttribute('aria-expanded', String(open)); };
  btn.addEventListener('click', e => { e.stopPropagation(); set(!menu.classList.contains('open')); });
  menu.addEventListener('click', () => set(false));
  document.addEventListener('click', e => { if (!e.target.closest('.tb-more')) set(false); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') set(false); });
}
// Top bar: one row when everything fits, otherwise a tidy two-row layout (date
// controls on their own row). Measured from the items' natural widths, which
// don't change with the layout, so this never flip-flops.
{
  const tb = document.querySelector('.topbar');
  const natural = el => {
    if (!el || getComputedStyle(el).display === 'none') return null;
    if (el.id !== 'gantt-controls') return el.getBoundingClientRect().width;
    const kids = [...el.children].filter(c => getComputedStyle(c).display !== 'none');
    return kids.reduce((w, c) => w + c.getBoundingClientRect().width, 0) + Math.max(0, kids.length - 1) * (parseFloat(getComputedStyle(el).columnGap) || 0);
  };
  const fit = () => {
    if (!tb) return;
    const cs = getComputedStyle(tb), gap = parseFloat(cs.columnGap) || 0;
    const ws = [...tb.children].map(natural).filter(w => w != null);
    const need = ws.reduce((a, b) => a + b, 0) + gap * Math.max(0, ws.length - 1) + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    tb.classList.toggle('two-rows', need > tb.clientWidth + 0.5);
  };
  window.addEventListener('resize', fit);
  document.fonts?.ready?.then(fit);
  // Tabs appearing (e.g. Leave for HR) or the version label changing width.
  if (window.ResizeObserver) { const ro = new ResizeObserver(fit); tb?.querySelectorAll('.view-tabs, .tb-logo, #gantt-controls').forEach(el => ro.observe(el)); }
  fit();
}
initTravelImport();
initCatManager();
initTagInputs();
initNavigation();
initResourceModal();
initCsv();
initImport();
initWhatsNew();
initKeyboard();
$('btn-add').addEventListener('click', () => openNew('', '', ''));

// Establish the default (Planner) view chrome.
setView('gantt');

// Auth last: a restored session immediately loads data and builds the views.
initAuth();
